import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, Car } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePricing } from '../services/pricingService';

const DrivewayCalculatorPanel = forwardRef(({
  isOpen, onToggle, measurementTotals, manualOverride, setManualOverride,
  onServiceTotalChange, initialInputs, onInputsChange,
}, ref) => {
  const [drivewayData, setDrivewayData] = useState(
    initialInputs && Object.keys(initialInputs).length
      ? { sqFt: '', surfaceType: 'concrete', discount: '', ...initialInputs }
      : { sqFt: '', surfaceType: 'concrete', discount: '' }
  );

  const onInputsChangeRef = useRef(onInputsChange);
  onInputsChangeRef.current = onInputsChange;
  useEffect(() => { onInputsChangeRef.current?.('driveway', drivewayData); }, [drivewayData]);

  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const area = measurementTotals?.areaSqFt || 0;
    // Only auto-fill if: area > 0 AND user hasn't manually overridden
    if (area > 0 && !manualOverride.drivewaySqFt) {
      // Round to whole number for clean UX display
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync from external measurement source
      setDrivewayData((prev) => ({ ...prev, sqFt: Math.round(area) }));
    }
  }, [isOpen, measurementTotals?.areaSqFt, manualOverride.drivewaySqFt]);

  const handleCalculate = async () => {
    const sq = Number(drivewayData.sqFt);
    if (!Number.isFinite(sq) || sq <= 0) {
      toast.error('Please enter driveway square footage.');
      return;
    }

    const resp = await calculatePricing('driveway', {
      data: {
        sqFt: sq,
      },
      discount: Number(drivewayData.discount) || 0,
    });

    if (!resp.success || !resp.quote) {
      toast.error(resp.message || 'Service pricing not implemented yet, but UI remains active.');
      onServiceTotalChange('driveway', { subtotal: 0, tax: 0, grandTotal: 0 });
      return;
    }

    const quote = resp.quote;
    const nowIso = new Date().toISOString();

    onServiceTotalChange('driveway', {
      input: {
        sqFt: sq,
        // surfaceType currently not used by backend config but kept for UI
      },
      result: {
        serviceTotal: quote.serviceTotal || quote.subtotal || 0,
        adjustedSqFt: quote.adjustedSqFt || sq,
        discount: quote.discount || 0,
        tax: quote.tax || 0,
        subtotal: quote.subtotal || quote.serviceTotal || 0,
        grandTotal: quote.grandTotal || 0,
      },
      measurement: {
        polygons: null,
        linearFeet: null,
        sqFt: sq,
        computedAt: nowIso,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    recalc: handleCalculate,
    getCurrentInputs: () => drivewayData,
  }), [drivewayData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Car className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">🚗 Driveways / Walkways</h3>
              <p className="text-sm text-muted-foreground">Configure surface specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Measured Area */}
            <div className="space-y-2">
              <Label htmlFor="driveway-area">Measured Area (sq ft)</Label>
              <Input
                id="driveway-area"
                type="number"
                placeholder="Enter square footage"
                className="font-mono"
                value={drivewayData.sqFt}
                onChange={(e) => {
                  setManualOverride(prev => ({ ...prev, drivewaySqFt: true }));
                  setDrivewayData(prev => ({ ...prev, sqFt: e.target.value }));
                }}
              />
            </div>

            {/* Surface Type */}
            <div className="space-y-2">
              <Label htmlFor="driveway-surface">Surface Type</Label>
              <Select>
                <SelectTrigger id="driveway-surface">
                  <SelectValue placeholder="Select surface" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="concrete">Concrete</SelectItem>
                  <SelectItem value="asphalt">Asphalt</SelectItem>
                  <SelectItem value="pavers">Pavers</SelectItem>
                  <SelectItem value="gravel">Gravel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
            >
              Calculate Surface Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

export default DrivewayCalculatorPanel;
